import { inject, Injectable, signal, Signal, WritableSignal } from '@angular/core';
import {
  KpActionState,
  KpFeatureMeta,
  SelectedSurveyMenu
} from '@app/core/shared/components/field-view/models/field-view';
import { Feature } from 'ol';
import { Units } from 'ol/control/ScaleLine';
import { LineString } from 'ol/geom';
import { gebcoBathyBaseMapLayers } from '../layers/gebco-bathy-base-map.layer';
import { styleFactory } from '../layers/interactive-kp.layer';
import TimestampUtility from '../utilities/timestamp.utility';
import { OpenWeatherBaseMapLayerService } from './open-weather-base-map-layer.service';
import { SignalBroadcastChannelService } from './signal-broadcast-channel.service';

@Injectable({ providedIn: 'root' })
export class FieldViewControlService {
  private static readonly CHANNELS = {
    graticuleVisibility: 'field-view-graticule-visibility',
    seaMapVisibility: 'field-view-sea-map-visibility',
    eventShapesVisibility: 'field-view-event-shapes-visibility',
    kpSpacing: 'field-view-kp-spacing',
    mapScaleUnit: 'field-view-map-scale-unit',
    selectedBathyBaseMapLayer: 'field-view-selected-bathy-base-map-layer',
    enableMeasure: 'field-view-enable-measure',
    clearMeasure: 'field-view-clear-measure',
    selectedEventSurveyLayers: 'field-view-selected-event-survey-layers',
    selectedBathySurveyLayers: 'field-view-selected-bathy-survey-layers',
    kpActionButtonState: 'field-view-kp-action-button-state',
    selectedWeatherBaseMapLayer: 'field-view-selected-weather-base-map-layer',
    weatherUnixTime: 'field-view-weather-unix-time',
    kpRangeClicked: 'field-view-kp-range-clicked',
    kpFeaturesMeta: 'field-view-kp-features-meta'
  };

  private readonly openWeatherBaseMapLayerService = inject(OpenWeatherBaseMapLayerService);
  private readonly broadcastService = inject(SignalBroadcastChannelService);

  private readonly _defaultValues = {
    graticuleVisibility: true,
    seaMapVisibility: false,
    eventShapesVisibility: false,
    kpSpacing: 0,
    mapScaleUnit: 'metric' as Units,
    selectedBathyBaseMapLayer: gebcoBathyBaseMapLayers[0].layer,
    enableMeasure: false,
    clearMeasure: false,
    selectedEventSurveyLayers: [],
    selectedBathySurveyLayers: [],
    kpActionButtonState: { clickedKpIndex: -1, totalKPFeatures: 0, navigation: '' } as KpActionState,
    selectedWeatherBaseMapLayer: this.openWeatherBaseMapLayerService.openWeatherBaseMapLayers[0].layer,
    weatherUnixTime: TimestampUtility.dateTimeNowAsTimestamp()
  };

  private readonly _graticuleVisibility: WritableSignal<boolean>;
  private readonly _broadcastGraticuleVisibility: (value: boolean) => void;

  private readonly _seaMapVisibility: WritableSignal<boolean>;
  private readonly _broadcastSeaMapVisibility: (value: boolean) => void;

  private readonly _eventShapesVisibility: WritableSignal<boolean>;
  private readonly _broadcastEventShapesVisibility: (value: boolean) => void;

  private readonly _kpSpacing: WritableSignal<number>;
  private readonly _broadcastKpSpacing: (value: number) => void;

  private readonly _mapScaleUnit: WritableSignal<Units>;
  private readonly _broadcastMapScaleUnit: (value: Units) => void;

  private readonly _selectedBathyBaseMapLayer: WritableSignal<string>;
  private readonly _broadcastSelectedBathyBaseMapLayer: (value: string) => void;

  private readonly _enableMeasure: WritableSignal<boolean>;
  private readonly _broadcastEnableMeasure: (value: boolean) => void;

  private readonly _clearMeasure: WritableSignal<boolean>;
  private readonly _broadcastClearMeasure: (value: boolean) => void;

  private readonly _selectedEventSurveyLayers: WritableSignal<SelectedSurveyMenu[]>;
  private readonly _broadcastSelectedEventSurveyLayers: (value: SelectedSurveyMenu[]) => void;

  private readonly _selectedBathySurveyLayers: WritableSignal<SelectedSurveyMenu[]>;
  private readonly _broadcastSelectedBathySurveyLayers: (value: SelectedSurveyMenu[]) => void;

  private readonly _kpActionButtonState: WritableSignal<KpActionState>;
  private readonly _broadcastKpActionButtonState: (value: KpActionState) => void;

  private readonly _selectedWeatherBaseMapLayer: WritableSignal<string>;
  private readonly _broadcastSelectedWeatherBaseMapLayer: (value: string) => void;

  private readonly _weatherUnixTime: WritableSignal<number>;
  private readonly _broadcastWeatherUnixTime: (value: number) => void;

  private readonly _kpRangeClickedSignal: WritableSignal<{ kpFrom: number; kpTo: number } | undefined>;
  private readonly _broadcastKpRangeClickedSignal: (
    value: { kpFrom: number; kpTo: number } | undefined
  ) => void;

  private readonly _kpFeaturesMetaSignal: WritableSignal<KpFeatureMeta[]>;
  private readonly _broadcastKpFeaturesMetaSignal: (value: KpFeatureMeta[]) => void;

  private readonly _kpFeaturesLocalSignal: WritableSignal<Feature<LineString>[]> = signal([]);

  constructor() {
    {
      const { signal, broadcastUpdate } = this.broadcastService.createSynchronizedSignal<boolean>(
        FieldViewControlService.CHANNELS.graticuleVisibility,
        this._defaultValues.graticuleVisibility
      );
      this._graticuleVisibility = signal;
      this._broadcastGraticuleVisibility = broadcastUpdate;
    }

    {
      const { signal, broadcastUpdate } = this.broadcastService.createSynchronizedSignal<boolean>(
        FieldViewControlService.CHANNELS.seaMapVisibility,
        this._defaultValues.seaMapVisibility
      );
      this._seaMapVisibility = signal;
      this._broadcastSeaMapVisibility = broadcastUpdate;
    }

    {
      const { signal, broadcastUpdate } = this.broadcastService.createSynchronizedSignal<boolean>(
        FieldViewControlService.CHANNELS.eventShapesVisibility,
        this._defaultValues.eventShapesVisibility
      );
      this._eventShapesVisibility = signal;
      this._broadcastEventShapesVisibility = broadcastUpdate;
    }

    {
      const { signal, broadcastUpdate } = this.broadcastService.createSynchronizedSignal<number>(
        FieldViewControlService.CHANNELS.kpSpacing,
        this._defaultValues.kpSpacing
      );
      this._kpSpacing = signal;
      this._broadcastKpSpacing = broadcastUpdate;
    }

    {
      const { signal, broadcastUpdate } = this.broadcastService.createSynchronizedSignal<Units>(
        FieldViewControlService.CHANNELS.mapScaleUnit,
        this._defaultValues.mapScaleUnit
      );
      this._mapScaleUnit = signal;
      this._broadcastMapScaleUnit = broadcastUpdate;
    }

    {
      const { signal, broadcastUpdate } = this.broadcastService.createSynchronizedSignal<string>(
        FieldViewControlService.CHANNELS.selectedBathyBaseMapLayer,
        this._defaultValues.selectedBathyBaseMapLayer
      );
      this._selectedBathyBaseMapLayer = signal;
      this._broadcastSelectedBathyBaseMapLayer = broadcastUpdate;
    }

    {
      const { signal, broadcastUpdate } = this.broadcastService.createSynchronizedSignal<boolean>(
        FieldViewControlService.CHANNELS.enableMeasure,
        this._defaultValues.enableMeasure
      );
      this._enableMeasure = signal;
      this._broadcastEnableMeasure = broadcastUpdate;
    }

    {
      const { signal, broadcastUpdate } = this.broadcastService.createSynchronizedSignal<boolean>(
        FieldViewControlService.CHANNELS.clearMeasure,
        this._defaultValues.clearMeasure
      );
      this._clearMeasure = signal;
      this._broadcastClearMeasure = broadcastUpdate;
    }

    {
      const { signal, broadcastUpdate } = this.broadcastService.createSynchronizedSignal<
        SelectedSurveyMenu[]
      >(
        FieldViewControlService.CHANNELS.selectedEventSurveyLayers,
        this._defaultValues.selectedEventSurveyLayers
      );
      this._selectedEventSurveyLayers = signal;
      this._broadcastSelectedEventSurveyLayers = broadcastUpdate;
    }

    {
      const { signal, broadcastUpdate } = this.broadcastService.createSynchronizedSignal<
        SelectedSurveyMenu[]
      >(
        FieldViewControlService.CHANNELS.selectedBathySurveyLayers,
        this._defaultValues.selectedBathySurveyLayers
      );
      this._selectedBathySurveyLayers = signal;
      this._broadcastSelectedBathySurveyLayers = broadcastUpdate;
    }

    {
      const { signal, broadcastUpdate } = this.broadcastService.createSynchronizedSignal<KpActionState>(
        FieldViewControlService.CHANNELS.kpActionButtonState,
        this._defaultValues.kpActionButtonState
      );
      this._kpActionButtonState = signal;
      this._broadcastKpActionButtonState = broadcastUpdate;
    }

    {
      const { signal, broadcastUpdate } = this.broadcastService.createSynchronizedSignal<string>(
        FieldViewControlService.CHANNELS.selectedWeatherBaseMapLayer,
        this._defaultValues.selectedWeatherBaseMapLayer
      );
      this._selectedWeatherBaseMapLayer = signal;
      this._broadcastSelectedWeatherBaseMapLayer = broadcastUpdate;
    }

    {
      const { signal, broadcastUpdate } = this.broadcastService.createSynchronizedSignal<number>(
        FieldViewControlService.CHANNELS.weatherUnixTime,
        this._defaultValues.weatherUnixTime
      );
      this._weatherUnixTime = signal;
      this._broadcastWeatherUnixTime = broadcastUpdate;
    }
    {
      const { signal, broadcastUpdate } = this.broadcastService.createSynchronizedSignal<
        { kpFrom: number; kpTo: number } | undefined
      >(FieldViewControlService.CHANNELS.kpRangeClicked, undefined);
      this._kpRangeClickedSignal = signal;
      this._broadcastKpRangeClickedSignal = broadcastUpdate;
    }
    {
      const { signal, broadcastUpdate } = this.broadcastService.createSynchronizedSignal<KpFeatureMeta[]>(
        FieldViewControlService.CHANNELS.kpFeaturesMeta,
        []
      );
      this._kpFeaturesMetaSignal = signal;
      this._broadcastKpFeaturesMetaSignal = broadcastUpdate;
    }
  }

  get graticuleVisibility(): Signal<boolean> {
    return this._graticuleVisibility;
  }
  get seaMapVisibility(): Signal<boolean> {
    return this._seaMapVisibility;
  }
  get eventShapesVisibility(): Signal<boolean> {
    return this._eventShapesVisibility;
  }
  get kpSpacing(): Signal<number> {
    return this._kpSpacing;
  }
  get mapScaleUnit(): Signal<Units> {
    return this._mapScaleUnit;
  }
  get selectedBathyBaseMapLayer(): Signal<string> {
    return this._selectedBathyBaseMapLayer;
  }
  get enableMeasure(): Signal<boolean> {
    return this._enableMeasure;
  }
  get clearMeasure(): Signal<boolean> {
    return this._clearMeasure;
  }
  get eventSurveyLayersSelection(): Signal<SelectedSurveyMenu[]> {
    return this._selectedEventSurveyLayers;
  }
  get bathySurveyLayersSelection(): Signal<SelectedSurveyMenu[]> {
    return this._selectedBathySurveyLayers;
  }
  get kpActionButtonState(): Signal<KpActionState> {
    return this._kpActionButtonState;
  }
  get selectedWeatherBaseMapLayer(): Signal<string> {
    return this._selectedWeatherBaseMapLayer;
  }
  get weatherUnixTime(): Signal<number> {
    return this._weatherUnixTime;
  }
  get kpRangeClickedSignal(): Signal<{ kpFrom: number; kpTo: number } | undefined> {
    return this._kpRangeClickedSignal.asReadonly();
  }
  get kpFeatures(): Signal<Feature<LineString>[]> {
    return this._kpFeaturesLocalSignal.asReadonly();
  }
  get kpFeaturesMeta(): Signal<KpFeatureMeta[]> {
    return this._kpFeaturesMetaSignal.asReadonly();
  }

  setGraticuleVisibility(value: boolean): void {
    this._graticuleVisibility.set(value);
    this._broadcastGraticuleVisibility(value);
  }
  setSeaMapVisibility(value: boolean): void {
    this._seaMapVisibility.set(value);
    this._broadcastSeaMapVisibility(value);
  }
  setEventShapesVisibility(value: boolean): void {
    this._eventShapesVisibility.set(value);
    this._broadcastEventShapesVisibility(value);
  }
  setKpSpacing(value: number): void {
    this._kpSpacing.set(value);
    this._broadcastKpSpacing(value);
  }
  setMapScaleUnit(value: Units): void {
    this._mapScaleUnit.set(value);
    this._broadcastMapScaleUnit(value);
  }
  setSelectedBathyBaseMapLayer(value: string): void {
    this._selectedBathyBaseMapLayer.set(value);
    this._broadcastSelectedBathyBaseMapLayer(value);
  }
  setEnableMeasure(value: boolean): void {
    this._enableMeasure.set(value);
    this._broadcastEnableMeasure(value);
  }
  setClearMeasure(value: boolean): void {
    this._clearMeasure.set(value);
    this._broadcastClearMeasure(value);
  }
  setKpActionButtonState(state: KpActionState): void {
    this._kpActionButtonState.set(state);
    this._broadcastKpActionButtonState(state);
  }
  setSelectedWeatherBaseMapLayer(value: string): void {
    this._selectedWeatherBaseMapLayer.set(value);
    this._broadcastSelectedWeatherBaseMapLayer(value);
  }
  setWeatherUnixTime(value: number): void {
    this._weatherUnixTime.set(value);
    this._broadcastWeatherUnixTime(value);
  }
  setKpRangeClicked(value: { kpFrom: number; kpTo: number } | undefined): void {
    this._kpRangeClickedSignal.set(value);
    this._broadcastKpRangeClickedSignal(value);
  }
  setKpFeatures(features: Feature<LineString>[]): void {
    this._kpFeaturesLocalSignal.set(features);
    const meta = features.map(f => this.featureToMeta(f));
    this._kpFeaturesMetaSignal.set(meta);
    this._broadcastKpFeaturesMetaSignal(meta);
  }

  syncKpFeaturesFromMeta(): void {
    const metaArr = this._kpFeaturesMetaSignal();
    const features = metaArr.map(meta => this.metaToFeature(meta));
    this._kpFeaturesLocalSignal.set(features);
  }

  private featureToMeta(feature: Feature<LineString>): KpFeatureMeta {
    return {
      start: feature.get('start'),
      end: feature.get('end'),
      kpFrom: feature.get('kpFrom'),
      kpTo: feature.get('kpTo'),
      label: feature.get('label')
    };
  }

  private metaToFeature(meta: KpFeatureMeta): Feature<LineString> {
    const line = new LineString([meta.start, meta.end]);
    const feature = new Feature(line);
    feature.set('start', meta.start);
    feature.set('end', meta.end);
    feature.set('kpFrom', meta.kpFrom);
    feature.set('kpTo', meta.kpTo);
    feature.set('label', meta.label);
    feature.setStyle(styleFactory(meta));
    return feature;
  }

  // Layer Selection
  private resetLayerSelection(layerSignal: WritableSignal<SelectedSurveyMenu[]>): void {
    const updated = layerSignal().map(layer => ({ ...layer, checked: false }));
    layerSignal.set(updated);
    if (layerSignal === this._selectedEventSurveyLayers) {
      this._broadcastSelectedEventSurveyLayers(updated);
    } else if (layerSignal === this._selectedBathySurveyLayers) {
      this._broadcastSelectedBathySurveyLayers(updated);
    }
  }

  private updateLayerSelection(
    layerSignal: WritableSignal<SelectedSurveyMenu[]>,
    selectedSurveyMenu: SelectedSurveyMenu
  ): void {
    const selectedLayers = layerSignal();
    const existingLayerIndex = selectedLayers.findIndex(
      layer => layer.pipelineSurveyId === selectedSurveyMenu.pipelineSurveyId
    );
    let updated: SelectedSurveyMenu[];
    if (existingLayerIndex !== -1) {
      updated = selectedLayers.map((layer, index) =>
        index === existingLayerIndex ? { ...layer, checked: selectedSurveyMenu.checked } : layer
      );
    } else {
      updated = [...selectedLayers, selectedSurveyMenu];
    }
    layerSignal.set(updated);
    if (layerSignal === this._selectedEventSurveyLayers) {
      this._broadcastSelectedEventSurveyLayers(updated);
    } else if (layerSignal === this._selectedBathySurveyLayers) {
      this._broadcastSelectedBathySurveyLayers(updated);
    }
  }

  updateEventSurveyLayerSelection(selectedSurveyMenu: SelectedSurveyMenu): void {
    this.updateLayerSelection(this._selectedEventSurveyLayers, selectedSurveyMenu);
  }
  updateBathyLayerSelection(selectedSurveyMenu: SelectedSurveyMenu): void {
    this.updateLayerSelection(this._selectedBathySurveyLayers, selectedSurveyMenu);
  }
  resetEventSurveyLayerSelection(): void {
    this.resetLayerSelection(this._selectedEventSurveyLayers);
  }
  resetBathyLayerSelection(): void {
    this.resetLayerSelection(this._selectedBathySurveyLayers);
  }

  // Panel Resets
  resetControlsPanel(): void {
    this._graticuleVisibility.set(this._defaultValues.graticuleVisibility);
    this._mapScaleUnit.set(this._defaultValues.mapScaleUnit);
    this._kpActionButtonState.set(this._defaultValues.kpActionButtonState);
    this._kpSpacing.set(this._defaultValues.kpSpacing);
  }
  resetDataPanel(): void {
    this.resetEventSurveyLayerSelection();
    this.resetBathyLayerSelection();
  }
  resetLayersPanel(): void {
    this._seaMapVisibility.set(this._defaultValues.seaMapVisibility);
    this._selectedBathyBaseMapLayer.set(this._defaultValues.selectedBathyBaseMapLayer);
  }
  resetWeatherPanel(): void {
    this._selectedWeatherBaseMapLayer.set(this._defaultValues.selectedWeatherBaseMapLayer);
    this._weatherUnixTime.set(this._defaultValues.weatherUnixTime);
  }
  resetEventsPanel(): void {
    this._eventShapesVisibility.set(this._defaultValues.eventShapesVisibility);
  }
  resetMeasurePanel(): void {
    this._enableMeasure.set(this._defaultValues.enableMeasure);
    this._clearMeasure.set(this._defaultValues.clearMeasure);
  }
  resetAllToDefault(): void {
    this.resetControlsPanel();
    this.resetDataPanel();
    this.resetLayersPanel();
    this.resetWeatherPanel();
    this.resetEventsPanel();
    this.resetMeasurePanel();
  }
}
